import React, { useEffect } from 'react';
import { Form, Input, Select, Checkbox } from 'antd';
import Map2 from "./../Map/Map2";
import { useTranslation } from 'react-i18next';
import GeocodingButton from "../../components/GeocodingButton";

const { Option } = Select;

const EnterpriseInfo = (props) => {
  const [form] = Form.useForm();
  const isEditMode = props.isEditMode || false;
  const { t } = useTranslation();

  // TODO: REMOVE AFTER DATA MIGRATION (added 2025-01-03)
  // This mapping is temporary for migrating old country names to codes
  // Once all database records use country codes (IT, FR, etc), remove:
  // 1. This countryNameToCode object
  // 2. The normalizeCountry function
  // 3. All normalizeCountry() calls in form initialization
  // Query to check migration status: SELECT DISTINCT country FROM enterprises WHERE LENGTH(country) > 2;
  const countryNameToCode = {
    'italy': 'IT', 'italia': 'IT',
    'france': 'FR', 'francia': 'FR',
    'germany': 'DE', 'germania': 'DE', 'deutschland': 'DE',
    'spain': 'ES', 'spagna': 'ES', 'españa': 'ES',
    'portugal': 'PT', 'portogallo': 'PT',
    'netherlands': 'NL', 'olanda': 'NL', 'paesi bassi': 'NL', 'holland': 'NL',
    'belgium': 'BE', 'belgio': 'BE', 'belgique': 'BE',
    'austria': 'AT', 'österreich': 'AT',
    'switzerland': 'CH', 'svizzera': 'CH', 'schweiz': 'CH', 'suisse': 'CH',
    'united kingdom': 'GB', 'regno unito': 'GB', 'uk': 'GB', 'great britain': 'GB',
    'ireland': 'IE', 'irlanda': 'IE', 'eire': 'IE',
    'denmark': 'DK', 'danimarca': 'DK', 'danmark': 'DK',
    'sweden': 'SE', 'svezia': 'SE', 'sverige': 'SE',
    'norway': 'NO', 'norvegia': 'NO', 'norge': 'NO',
    'finland': 'FI', 'finlandia': 'FI', 'suomi': 'FI',
    'poland': 'PL', 'polonia': 'PL', 'polska': 'PL',
    'czech republic': 'CZ', 'repubblica ceca': 'CZ', 'cechia': 'CZ', 'czechia': 'CZ',
    'hungary': 'HU', 'ungheria': 'HU', 'magyarország': 'HU',
    'romania': 'RO', 'românia': 'RO',
    'bulgaria': 'BG', 'българия': 'BG',
    'croatia': 'HR', 'croazia': 'HR', 'hrvatska': 'HR',
    'slovenia': 'SI', 'slovenija': 'SI',
    'slovakia': 'SK', 'slovacchia': 'SK', 'slovensko': 'SK',
    'greece': 'GR', 'grecia': 'GR', 'hellas': 'GR', 'ellada': 'GR',
    'georgia': 'GE', 'sakartvelo': 'GE',
    'turkey': 'TR', 'turchia': 'TR', 'türkiye': 'TR',
    'armenia': 'AM', 'hayastan': 'AM',
    'azerbaijan': 'AZ', 'azərbaycan': 'AZ'
  };

  // TODO: REMOVE AFTER DATA MIGRATION - Helper function to normalize country value
  const normalizeCountry = (country) => {
    if (!country) return null;
    
    // If it's already a valid 2-letter code, return it
    if (country.length === 2 && /^[A-Z]{2}$/i.test(country)) {
      return country.toUpperCase();
    }
    
    // Try to convert country name to code
    const normalized = country.toLowerCase().trim();
    return countryNameToCode[normalized] || country; // Return original if not found
  };

  useEffect(() => {
    if (props.record.enterprise && Object.keys(props.record.enterprise).length > 0) {
      form.setFieldsValue({
        vat: props.record.enterprise.vat,
        name: props.record.enterprise.name,
        country: normalizeCountry(props.record.enterprise.country), // TODO: REMOVE normalizeCountry() after migration
        county: props.record.enterprise.county,
        town: props.record.enterprise.town,
        postcode: props.record.enterprise.postcode,
        road: props.record.enterprise.road,
        houseNumber: props.record.enterprise.houseNumber,
        description: props.record.enterprise.description,
        geometry: props.record.enterprise.geometry,
        managementType: props.record.enterprise.managementType,
        coordinatesInferred: props.record.enterprise.coordinatesInferred || false
      });
    } else {
      // Clear all form fields when no enterprise is selected
      form.resetFields();
    }
  }, [props.record.enterprise, form]);

  // Reset validation when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      form.resetFields();

      if (props.record.enterprise) {
        form.setFieldsValue({
          vat: props.record.enterprise.vat,
          name: props.record.enterprise.name,
          country: normalizeCountry(props.record.enterprise.country), // TODO: REMOVE normalizeCountry() after migration
          county: props.record.enterprise.county,
          town: props.record.enterprise.town,
          postcode: props.record.enterprise.postcode,
          road: props.record.enterprise.road,
          houseNumber: props.record.enterprise.houseNumber,
          description: props.record.enterprise.description,
          geometry: props.record.enterprise.geometry,
          managementType: props.record.enterprise.managementType,
          coordinatesInferred: props.record.enterprise.coordinatesInferred || false
        });
      }
    }
  }, [isEditMode, form, props.record.enterprise]);

  const formItemLayout = {
    labelCol: {
      sm: { span: 2 },
    },
    wrapperCol: {
      sm: { span: 22 },
    },
  };

  const buildAddress = (address) => {
    form.setFieldsValue({
      country: address.country,
      county: address.county,
      town: address.town || address.city,
      postcode: address.postcode,
      road: address.road,
      houseNumber: address.houseNumber
    });
  };

  // Handle form changes and notify parent
  const handleFormChange = () => {
    const values = form.getFieldsValue();

    // Format for parent component
    const formattedFields = {};
    Object.keys(values).forEach(key => {
      formattedFields[key] = { value: values[key] };
    });

    // Only check validation in edit mode
    let isValid = true;
    if (isEditMode) {
      // Check for existing errors without triggering new validation
      const errors = form.getFieldsError();
      const hasErrors = errors.some(error => error.errors.length > 0);

      // Check for required empty fields
      const requiredFields = ['name', 'country', 'town'];
      const hasEmptyRequired = requiredFields.some(field =>
        !values[field] || (typeof values[field] === 'string' && values[field].trim() === '')
      );

      isValid = !hasErrors && !hasEmptyRequired;
    }

    // Notify parent
    props.parentCallback(formattedFields, isValid);
  };

  const handleGeocode = (result) => {
    // Update geometry with the geocoded coordinates
    if (result.latitude && result.longitude) {
      const geometry = {
        type: "Point",
        coordinates: [result.longitude, result.latitude]
      };
      
      // Convert geometry to JSON string as expected by Map2
      const geometryStr = JSON.stringify(geometry);
      form.setFieldValue('geometry', geometryStr);
      
      // Mark coordinates as inferred since they came from geocoding API
      form.setFieldValue('coordinatesInferred', true);
      
      // Trigger form change to update parent
      handleFormChange();
    }
  };

  return (
    <Form
      form={form}
      {...formItemLayout}
      onValuesChange={handleFormChange}
      onFieldsChange={handleFormChange}
    >
      <Form.Item
        label="VAT"
        name="vat"
        rules={isEditMode ? [{ required: false, message: 'Please input VAT!' }] : []}
      >
        <Input placeholder="VAT" disabled={!isEditMode} />
      </Form.Item>

      <Form.Item
        label="Name"
        name="name"
        rules={isEditMode ? [{ required: true, message: 'Please input Name!' }] : []}
      >
        <Input placeholder="Name" disabled={!isEditMode} />
      </Form.Item>

      <Form.Item label="Address" style={{ marginBottom: 0 }}>
        <Form.Item
          name="country"
          rules={isEditMode ? [{ required: true, message: 'Please select country!' }] : []}
          style={{ display: 'inline-block', width: 'calc(10% - 12px)' }}
        >
          <Select
            placeholder="Country *"
            disabled={!isEditMode}
            showSearch
            optionFilterProp="children"
          >
            {Object.entries(t('countries', { returnObjects: true })).map(([code, name]) => (
              <Option key={code} value={code}>
                {name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <span style={{ display: 'inline-block', width: '24px', textAlign: 'center' }}></span>

        <Form.Item
          name="county"
          rules={isEditMode ? [{ required: false, message: 'Please input county!' }] : []}
          style={{ display: 'inline-block', width: 'calc(10% - 12px)' }}
        >
          <Input placeholder="County" disabled={!isEditMode} />
        </Form.Item>

        <span style={{ display: 'inline-block', width: '24px', textAlign: 'center' }}></span>

        <Form.Item
          name="town"
          rules={isEditMode ? [{ required: true, message: 'Please input town!' }] : []}
          style={{ display: 'inline-block', width: 'calc(10% - 12px)' }}
        >
          <Input placeholder="Town *" disabled={!isEditMode} />
        </Form.Item>
        
        <Form.Item noStyle shouldUpdate>
          {() => (
            <GeocodingButton
              value={form.getFieldValue('town')}
              type="city"
              country={form.getFieldValue('country') || 'IT'}
              onGeocode={handleGeocode}
              disabled={!isEditMode}
              style={{ marginLeft: 8 }}
            />
          )}
        </Form.Item>

        <span style={{ display: 'inline-block', width: '24px', textAlign: 'center' }}></span>

        <Form.Item
          name="postcode"
          rules={isEditMode ? [{ required: false, message: 'Please input post code!' }] : []}
          style={{ display: 'inline-block', width: 'calc(10% - 12px)' }}
        >
          <Input placeholder="Post code" disabled={!isEditMode} />
        </Form.Item>
        
        <Form.Item noStyle shouldUpdate>
          {() => (
            <GeocodingButton
              value={form.getFieldValue('postcode')}
              type="postcode"
              country={form.getFieldValue('country') || 'IT'}
              onGeocode={handleGeocode}
              disabled={!isEditMode}
              style={{ marginLeft: 8 }}
            />
          )}
        </Form.Item>

        <span style={{ display: 'inline-block', width: '24px', textAlign: 'center' }}></span>

        <Form.Item
          name="road"
          rules={isEditMode ? [{ required: false, message: 'Please input road!' }] : []}
          style={{ display: 'inline-block', width: 'calc(10% - 12px)' }}
        >
          <Input placeholder="Road" disabled={!isEditMode} />
        </Form.Item>

        <span style={{ display: 'inline-block', width: '24px', textAlign: 'center' }}></span>

        <Form.Item
          name="houseNumber"
          rules={isEditMode ? [{ required: false, message: 'Please input house number!' }] : []}
          style={{ display: 'inline-block', width: 'calc(10% - 12px)' }}
        >
          <Input placeholder="Number" disabled={!isEditMode} />
        </Form.Item>
      </Form.Item>

      <Form.Item
        label="Management"
        name="managementType"
        rules={isEditMode ? [{ required: false, message: 'Please select management type!' }] : []}
      >
        <Select disabled={!isEditMode}>
          {props.managementTypes.map(type => (
            <Option key={type} value={type}>
              {t(`enterprises.managementType.${type}`)}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Description"
        name="description"
        rules={isEditMode ? [{ required: false, message: 'Please input Description!' }] : []}
      >
        <Input placeholder="Description" disabled={!isEditMode} />
      </Form.Item>

      <Form.Item
        label="Position"
        name="geometry"
      >
        <Map2 name={props.type} onChangeAddress={(e) => buildAddress(e)} disabled={!isEditMode} />
      </Form.Item>

      <Form.Item
        name="coordinatesInferred"
        valuePropName="checked"
        style={{ marginLeft: '2%' }}
      >
        <Checkbox disabled={!isEditMode}>
          Coordinates are approximate/inferred (not exact GPS)
        </Checkbox>
      </Form.Item>
    </Form>
  );
};

export default EnterpriseInfo;